import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./scss/index.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { animated, useSpring } from "react-spring";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader } from "@components/atoms";
import { CheckoutProgressBar } from "@components/molecules";
import { CartSummary, PaymentGatewaysList, translateAdyenConfirmationError, adyenNotNegativeConfirmationStatusCodes } from "@components/organisms";
import { Checkout } from "@components/templates";
import { useAuth, useCart, useCheckout, useSaleorClient, useVouchersAvailableToUser } from "@saleor/sdk";
import { CHECKOUT_STEPS, CheckoutStep } from "@temp/core/config";
import { checkoutMessages } from "@temp/intl";
import { GatewayTypesEnum } from "@globalTypes/globalTypes";
import { useCheckoutPaymentGateways } from "@hooks/useCheckoutPaymentGateways";
import { createManualTransferCheckoutPayment } from "@hooks/useManualTransferCheckoutPayment";
import { syncSdkCheckoutPayment } from "../../utils/syncSdkCheckoutPayment";
import { parseQueryString } from "@temp/core/utils";
import { CurrentTradeDetailsContext } from "@temp/contexts";
import { AlertCheckoutErrors, AlertVouchersAvailableToUser, MainMenu, UpdateCheckout } from "../../../components";
import { CheckoutRouter } from "./CheckoutRouter";
import { CheckoutAddressSubpage, CheckoutPaymentSubpage, CheckoutReviewSubpage, CheckoutShippingSubpage } from "./subpages";

var prepareCartSummary = function prepareCartSummary(navigate, mobileCartOpened, setMobileCartOpened, totalPrice, subtotalPrice, shippingTaxedPrice, promoTaxedPrice, items, userDefaultProductImageThumbnail) {
  var products = React.useMemo(function () {
    return items === null || items === void 0 ? void 0 : items.map(function (_ref) {
      var _allToppingsName, _allToppingsName2, _variant$product, _variant$product2, _variant$pricing, _variant$pricing2, _variant$product3, _variant$product3$thu, _variant$product4, _variant$product4$thu;

      var id = _ref.id,
          attributes = _ref.attributes,
          variant = _ref.variant,
          totalPrice = _ref.totalPrice,
          totalPriceUndiscounted = _ref.totalPriceUndiscounted,
          quantity = _ref.quantity;
      var allToppingsName = [];

      if (typeof attributes === "string") {
        allToppingsName = JSON.parse(attributes);
      } else {
        allToppingsName = attributes;
      }

      allToppingsName = (_allToppingsName = allToppingsName) === null || _allToppingsName === void 0 ? void 0 : _allToppingsName.filter(function (topping) {
        return topping.totalAmount > 0;
      }).map(function (topping) {
        return topping.dataValues;
      }).reduce(function (a, b) {
        return a.concat(b);
      }, []).filter(function (value) {
        return value.quantity > 0;
      }).map(function (value) {
        return value.nameValue;
      }).join(", ");
      var ellipsisToppingsName = allToppingsName && ((_allToppingsName2 = allToppingsName) === null || _allToppingsName2 === void 0 ? void 0 : _allToppingsName2.length) >= 25 ? "".concat(allToppingsName.slice(0, 25), "...") : allToppingsName;
      return {
        lineId: id || "",
        name: variant.name ? variant.name : (variant === null || variant === void 0 ? void 0 : (_variant$product = variant.product) === null || _variant$product === void 0 ? void 0 : _variant$product.name) ? variant === null || variant === void 0 ? void 0 : (_variant$product2 = variant.product) === null || _variant$product2 === void 0 ? void 0 : _variant$product2.name : "",
        price: totalPrice,
        priceUndiscounted: totalPriceUndiscounted,
        productVariantId: variant.id,
        quantity: quantity,
        sku: variant.sku || "",
        subCategoryId: variant.product.category.id,
        toppingsName: ellipsisToppingsName,
        totalPriceWithOutToppingsCost: variant === null || variant === void 0 ? void 0 : (_variant$pricing = variant.pricing) === null || _variant$pricing === void 0 ? void 0 : _variant$pricing.price,
        totalPriceUndiscountedWithOutToppingsCost: variant === null || variant === void 0 ? void 0 : (_variant$pricing2 = variant.pricing) === null || _variant$pricing2 === void 0 ? void 0 : _variant$pricing2.priceUndiscounted,
        thumbnail: {
          alt: ((_variant$product3 = variant.product) === null || _variant$product3 === void 0 ? void 0 : (_variant$product3$thu = _variant$product3.thumbnail) === null || _variant$product3$thu === void 0 ? void 0 : _variant$product3$thu.alt) || (userDefaultProductImageThumbnail === null || userDefaultProductImageThumbnail === void 0 ? void 0 : userDefaultProductImageThumbnail.alt) || undefined,
          url: ((_variant$product4 = variant.product) === null || _variant$product4 === void 0 ? void 0 : (_variant$product4$thu = _variant$product4.thumbnail) === null || _variant$product4$thu === void 0 ? void 0 : _variant$product4$thu.url) || (userDefaultProductImageThumbnail === null || userDefaultProductImageThumbnail === void 0 ? void 0 : userDefaultProductImageThumbnail.url)
        }
      };
    });
  }, [items]);
  return React.createElement(CartSummary, {
    mobileCartOpened: mobileCartOpened,
    setMobileCartOpened: setMobileCartOpened,
    shipping: shippingTaxedPrice,
    subtotal: subtotalPrice,
    promoCode: promoTaxedPrice,
    total: totalPrice,
    products: products
  });
};

var getCheckoutProgress = function getCheckoutProgress(hasShippingMethod, loaded, activeStepIndex, steps) {
  return loaded ? React.createElement(CheckoutProgressBar, {
    steps: steps,
    activeStep: activeStepIndex,
    hasShippingMethod: hasShippingMethod
  }) : null;
};

var getButton = function getButton(text, onClick, scaleButton) {
  if (text) {
    return React.createElement(animated.button, {
      className: "buttonCheckAnimation",
      onClick: onClick,
      style: scaleButton,
      type: "submit"
    }, React.createElement("p", null, text));
  }

  return React.createElement(React.Fragment, null);
};

var CheckoutPage = function CheckoutPage() {
  var _checkout$shippingMet2, _checkout$availableSh, _user$currentTrade, _buttonText;

  var _useAuth = useAuth(),
      user = _useAuth.user;

  var location = useLocation();
  var navigate = useNavigate();
  var querystring = parseQueryString(location);

  var _useCart = useCart(),
      cartLoaded = _useCart.loaded,
      shippingPrice = _useCart.shippingPrice,
      discount = _useCart.discount,
      subtotalPrice = _useCart.subtotalPrice,
      totalPrice = _useCart.totalPrice,
      items = _useCart.items;

  var _useCheckout = useCheckout(),
      checkoutLoaded = _useCheckout.loaded,
      checkout = _useCheckout.checkout,
      payment = _useCheckout.payment,
      promoCodeDiscount = _useCheckout.promoCodeDiscount,
      availablePaymentGateways = _useCheckout.availablePaymentGateways,
      createPayment = _useCheckout.createPayment,
      completeCheckout = _useCheckout.completeCheckout,
      setShippingMethod = _useCheckout.setShippingMethod,
      updateCheckoutFromBD = _useCheckout.updateCheckoutFromBD;

  var saleor = useSaleorClient();

  var _useCheckoutPaymentGa = useCheckoutPaymentGateways(checkout === null || checkout === void 0 ? void 0 : checkout.token),
      checkoutPaymentGateways = _useCheckoutPaymentGa.paymentGateways;

  var intl = useIntl();

  var _useContext = useContext(CurrentTradeDetailsContext),
      dataCurrentTrade = _useContext.data,
      changeDataCurrentTrade = _useContext.changeData;

  if (cartLoaded && !checkoutLoaded && (!items || !(items === null || items === void 0 ? void 0 : items.length))) {
    return React.createElement(Navigate, {
      to: "/"
    });
  }

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      controlPaymentConfirm = _useState2[0],
      setControlPaymentConfirm = _useState2[1];

  var _useState3 = useState(""),
      _useState4 = _slicedToArray(_useState3, 2),
      inputPromoValue = _useState4[0],
      setInputPromoValue = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      mobileCartOpened = _useState6[0],
      setMobileCartOpened = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      loadingPromoCode = _useState8[0],
      setLoadingPromoCode = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      paymentConfirmation = _useState10[0],
      setPaymentConfirmation = _useState10[1];

  var _useState11 = useState(false),
      _useState12 = _slicedToArray(_useState11, 2),
      showSetPromo = _useState12[0],
      setshowSetPromo = _useState12[1];

  var _useState13 = useState(false),
      _useState14 = _slicedToArray(_useState13, 2),
      showVouchersList = _useState14[0],
      setShowVouchersList = _useState14[1];

  var _useState15 = useState(false),
      _useState16 = _slicedToArray(_useState15, 2),
      submitInProgress = _useState16[0],
      setSubmitInProgress = _useState16[1];

  var _useState17 = useState(false),
      _useState18 = _slicedToArray(_useState17, 2),
      updateCheckoutState = _useState18[0],
      setUpdateCheckoutState = _useState18[1];

  var _useState19 = useState(!!(promoCodeDiscount === null || promoCodeDiscount === void 0 ? void 0 : promoCodeDiscount.voucherCode)),
      _useState20 = _slicedToArray(_useState19, 2),
      showPromoCodeForm = _useState20[0],
      setShowPromoCodeForm = _useState20[1];

  var _useState21 = useState(true),
      _useState22 = _slicedToArray(_useState21, 2),
      pendingPromoCode = _useState22[0],
      setpendingPromoCode = _useState22[1];

  var _useState23 = useState(false),
      _useState24 = _slicedToArray(_useState23, 2),
      showErrors = _useState24[0],
      setShowErrors = _useState24[1];

  var _useState25 = useState(React.createElement(Loader, null)),
      _useState26 = _slicedToArray(_useState25, 2),
      viewRouter = _useState26[0],
      setViewRouter = _useState26[1];

  var _useVouchersAvailable = useVouchersAvailableToUser({
    fetchPolicy: "network-only",
    skip: !!(promoCodeDiscount === null || promoCodeDiscount === void 0 ? void 0 : promoCodeDiscount.voucherCode)
  }),
      vouchersData = _useVouchersAvailable.data,
      vouchersLoading = _useVouchersAvailable.loading;

  var _useState27 = useState(payment === null || payment === void 0 ? void 0 : payment.gateway),
      _useState28 = _slicedToArray(_useState27, 2),
      selectedPaymentGateway = _useState28[0],
      setSelectedPaymentGateway = _useState28[1];

  var _useState29 = useState(payment === null || payment === void 0 ? void 0 : payment.token),
      _useState30 = _slicedToArray(_useState29, 2),
      selectedPaymentGatewayToken = _useState30[0],
      setSelectedPaymentGatewayToken = _useState30[1];

  var _useState31 = useState([]),
      _useState32 = _slicedToArray(_useState31, 2),
      globalErrors = _useState32[0],
      setGlobalErrors = _useState32[1];

  var scaleButton = useSpring({
    config: {
      friction: 40,
      mass: 5,
      tension: 350
    },
    from: {
      scale: 1.1
    },
    loop: true,
    to: {
      scale: 1
    }
  });
  useEffect(function () {
    if (globalErrors && (globalErrors === null || globalErrors === void 0 ? void 0 : globalErrors.length) > 0) {
      setShowErrors(true);
    }

    return function () {
      setShowErrors(false);
    };
  }, [globalErrors]);
  useEffect(function () {
    setSelectedPaymentGateway(payment === null || payment === void 0 ? void 0 : payment.gateway);
    return function () {
      setSelectedPaymentGateway(undefined);
    };
  }, [payment === null || payment === void 0 ? void 0 : payment.gateway]);
  useEffect(function () {
    setSelectedPaymentGatewayToken(payment === null || payment === void 0 ? void 0 : payment.token);
    return function () {
      setSelectedPaymentGatewayToken(undefined);
    };
  }, [payment === null || payment === void 0 ? void 0 : payment.token]);
  useEffect(function () {
    var _CHECKOUT_STEPS$find;

    var paymentConfirmStepLink = (_CHECKOUT_STEPS$find = CHECKOUT_STEPS.find(function (step) {
      return step.step === CheckoutStep.PaymentConfirm;
    })) === null || _CHECKOUT_STEPS$find === void 0 ? void 0 : _CHECKOUT_STEPS$find.link;

    if (!submitInProgress && checkout && location.pathname === paymentConfirmStepLink && !paymentConfirmation) {
      handlePaymentConfirm();
    }

    return function () {
      setControlPaymentConfirm(true);
    };
  }, [location.pathname, querystring, submitInProgress, checkout]);
  useEffect(function () {
    var _vouchersData$voucher, _vouchersData$voucher2;

    if (checkoutLoaded && location.pathname === "/checkout/payment" && !vouchersLoading && (vouchersData === null || vouchersData === void 0 ? void 0 : vouchersData.vouchersAvailableToUser) && (vouchersData === null || vouchersData === void 0 ? void 0 : (_vouchersData$voucher = vouchersData.vouchersAvailableToUser) === null || _vouchersData$voucher === void 0 ? void 0 : _vouchersData$voucher.length) > 0 && !localStorage.getItem("updateCheckoutPayment")) {
      setShowVouchersList(!(checkoutLoaded && !!(payment === null || payment === void 0 ? void 0 : payment.gateway) || !!(promoCodeDiscount === null || promoCodeDiscount === void 0 ? void 0 : promoCodeDiscount.voucherCode)));
    } else if (checkoutLoaded && location.pathname === "/checkout/payment" && !vouchersLoading && (vouchersData === null || vouchersData === void 0 ? void 0 : vouchersData.vouchersAvailableToUser) && (vouchersData === null || vouchersData === void 0 ? void 0 : (_vouchersData$voucher2 = vouchersData.vouchersAvailableToUser) === null || _vouchersData$voucher2 === void 0 ? void 0 : _vouchersData$voucher2.length) === 0 && !localStorage.getItem("updateCheckoutPayment")) {
      setShowVouchersList(false);
    }

    if (checkoutLoaded && localStorage.getItem("updateCheckoutPayment")) {
      setShowVouchersList(false);
      localStorage.removeItem("updateCheckoutPayment");
    }

    if (location.pathname !== "/checkout/payment") {
      setShowPromoCodeForm(false);
    }

    return function () {};
  }, [checkoutLoaded, location.pathname, vouchersLoading]);
  var isShippingRequiredForProducts = items && items.some(function (_ref2) {
    var _variant$product5;

    var variant = _ref2.variant;
    return (_variant$product5 = variant.product) === null || _variant$product5 === void 0 ? void 0 : _variant$product5.productType.isShippingRequired;
  });
  var stepsWithViews = CHECKOUT_STEPS.filter(function (_ref3) {
    var withoutOwnView = _ref3.withoutOwnView;
    return !withoutOwnView;
  });
  var steps = isShippingRequiredForProducts ? stepsWithViews : stepsWithViews.filter(function (_ref4) {
    var onlyIfShippingRequired = _ref4.onlyIfShippingRequired;
    return !onlyIfShippingRequired;
  });

  var getActiveStepIndex = function getActiveStepIndex() {
    var matchingStepIndex = steps.findIndex(function (_ref5) {
      var link = _ref5.link;
      return location.pathname.includes(link);
    });
    return matchingStepIndex !== -1 ? matchingStepIndex : (steps === null || steps === void 0 ? void 0 : steps.length) - 1;
  };

  var getActiveStep = function getActiveStep() {
    return steps[getActiveStepIndex()];
  };

  var checkoutAddressSubpageRef = useRef(null);
  var checkoutShippingSubpageRef = useRef(null);
  var checkoutPaymentSubpageRef = useRef(null);
  var checkoutReviewSubpageRef = useRef(null);
  var checkoutGatewayFormId = "gateway-form";
  var checkoutGatewayFormRef = useRef(null);
  var promoCodeDiscountFormRef = useRef(null);
  var handleNextStepClick = React.useCallback(function (index, availableShippingMethodsLength) {
    var _checkoutAddressSubpa, _checkoutShippingSubp, _checkoutPaymentSubpa, _checkoutReviewSubpag;

    // Some magic above and below ensures that the activeStepIndex will always
    // be in 0-3 range

    /* eslint-disable default-case */
    switch (index) {
      case 0:
        if ((_checkoutAddressSubpa = checkoutAddressSubpageRef.current) === null || _checkoutAddressSubpa === void 0 ? void 0 : _checkoutAddressSubpa.submitAddress) {
          var _checkoutAddressSubpa2;

          (_checkoutAddressSubpa2 = checkoutAddressSubpageRef.current) === null || _checkoutAddressSubpa2 === void 0 ? void 0 : _checkoutAddressSubpa2.submitAddress();
        }

        break;

      case 1:
        if (((_checkoutShippingSubp = checkoutShippingSubpageRef.current) === null || _checkoutShippingSubp === void 0 ? void 0 : _checkoutShippingSubp.submitShipping) && availableShippingMethodsLength === 0) {
          navigate("/checkout/address");
        } else {
          var _checkoutShippingSubp2;

          (_checkoutShippingSubp2 = checkoutShippingSubpageRef.current) === null || _checkoutShippingSubp2 === void 0 ? void 0 : _checkoutShippingSubp2.submitShipping();
        }

        break;

      case 2:
        if ((_checkoutPaymentSubpa = checkoutPaymentSubpageRef.current) === null || _checkoutPaymentSubpa === void 0 ? void 0 : _checkoutPaymentSubpa.submitPayment) {
          var _checkoutPaymentSubpa2;

          (_checkoutPaymentSubpa2 = checkoutPaymentSubpageRef.current) === null || _checkoutPaymentSubpa2 === void 0 ? void 0 : _checkoutPaymentSubpa2.submitPayment();
        }

        break;

      case 3:
        if ((_checkoutReviewSubpag = checkoutReviewSubpageRef.current) === null || _checkoutReviewSubpag === void 0 ? void 0 : _checkoutReviewSubpag.complete) {
          var _checkoutReviewSubpag2;

          (_checkoutReviewSubpag2 = checkoutReviewSubpageRef.current) === null || _checkoutReviewSubpag2 === void 0 ? void 0 : _checkoutReviewSubpag2.complete();
        }

        break;
    }
  }, []);
  var handleStepSubmitSuccess = React.useCallback(function (CheckoutStep, currentStep, activeStepIndex, steps, data) {
    var _steps;

    if (currentStep === CheckoutStep.Review && data) {
      localStorage.removeItem("flag_history");
      localStorage.removeItem("flag_progress");
      localStorage.setItem("flag_basket", "true");
      return navigate("/status/".concat(data === null || data === void 0 ? void 0 : data.token));
    }

    return navigate((_steps = steps[activeStepIndex + 1]) === null || _steps === void 0 ? void 0 : _steps.link);
  }, []);
  var shippingTaxedPrice = React.useMemo(function () {
    var _checkout$shippingMet;

    return (checkout === null || checkout === void 0 ? void 0 : (_checkout$shippingMet = checkout.shippingMethod) === null || _checkout$shippingMet === void 0 ? void 0 : _checkout$shippingMet.id) && shippingPrice ? {
      gross: shippingPrice,
      net: shippingPrice
    } : null;
  }, [shippingPrice, checkout === null || checkout === void 0 ? void 0 : (_checkout$shippingMet2 = checkout.shippingMethod) === null || _checkout$shippingMet2 === void 0 ? void 0 : _checkout$shippingMet2.id]);
  var promoTaxedPrice = React.useMemo(function () {
    return discount && {
      gross: discount,
      net: discount
    };
  }, [discount]);
  var handleShowVoucherList = React.useCallback(function () {
    setShowVouchersList(true);
  }, [setShowVouchersList]);
  useEffect(function () {
    if (cartLoaded && checkoutLoaded) {
      setViewRouter(React.createElement(CheckoutRouter, {
        items: items,
        checkout: checkout,
        payment: payment,
        totalPrice: totalPrice,
        locationPrueba: location.pathname,
        RenderAddress: function RenderAddress(props) {
          return React.createElement(CheckoutAddressSubpage, _extends({
            ref: checkoutAddressSubpageRef,
            changeSubmitProgress: setSubmitInProgress,
            onSubmitSuccess: function onSubmitSuccess() {
              return handleStepSubmitSuccess(CheckoutStep, CheckoutStep.Address, getActiveStepIndex(), steps);
            }
          }, props));
        },
        RenderShipping: function RenderShipping(props) {
          return React.createElement(CheckoutShippingSubpage, _extends({
            ref: checkoutShippingSubpageRef,
            changeSubmitProgress: setSubmitInProgress,
            onSubmitSuccess: function onSubmitSuccess() {
              return handleStepSubmitSuccess(CheckoutStep, CheckoutStep.Shipping, getActiveStepIndex(), steps);
            }
          }, props));
        },
        RenderPayment: function RenderPayment(props) {
          return React.createElement(CheckoutPaymentSubpage, _extends({
            ref: checkoutPaymentSubpageRef,
            paymentGatewayFormRef: checkoutGatewayFormRef,
            promoCodeDiscountFormRef: promoCodeDiscountFormRef,
            changeSubmitProgress: setSubmitInProgress,
            onSubmitSuccess: function onSubmitSuccess() {
              return handleStepSubmitSuccess(CheckoutStep, CheckoutStep.Payment, getActiveStepIndex(), steps);
            },
            onPaymentGatewayError: setGlobalErrors,
            inputPromoValue: inputPromoValue,
            setshowSetPromo: setshowSetPromo,
            showSetPromo: showSetPromo,
            submitInProgress: submitInProgress,
            setShowPromoCodeForm: setShowPromoCodeForm,
            showPromoCodeForm: showPromoCodeForm,
            loadingPromoCode: loadingPromoCode,
            handleShowVoucherList: handleShowVoucherList,
            setLoadingPromoCode: setLoadingPromoCode,
            pendingPromoCode: pendingPromoCode,
            setpendingPromoCode: setpendingPromoCode,
            promoCodeErrors: globalErrors,
            setPromoCodeErrors: setGlobalErrors,
            vouchersData: vouchersData,
            vouchersLoading: vouchersLoading
          }, props));
        },
        RenderReview: function RenderReview(props) {
          return React.createElement(CheckoutReviewSubpage, _extends({
            ref: checkoutReviewSubpageRef,
            paymentGatewayFormRef: checkoutGatewayFormRef,
            selectedPaymentGateway: selectedPaymentGateway,
            selectedPaymentGatewayToken: selectedPaymentGatewayToken,
            changeSubmitProgress: setSubmitInProgress,
            setUpdateCheckoutState: setUpdateCheckoutState,
            onSubmitSuccess: function onSubmitSuccess(data) {
              return handleStepSubmitSuccess(CheckoutStep, CheckoutStep.Review, getActiveStepIndex(), steps, data);
            }
          }, props));
        }
      }));
    }

    return function () {
      setViewRouter(React.createElement(React.Fragment, null));
    };
  }, [cartLoaded, checkoutLoaded, loadingPromoCode, location.pathname, showPromoCodeForm, inputPromoValue, vouchersData, vouchersLoading]);
  useEffect(function () {
    localStorage.removeItem("checkout-modify");
    return function () {};
  }, []);

  var handleSetShippingMethod = /*#__PURE__*/function () {
    var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(shippingMethodId) {
      var _yield$setShippingMet, dataError, errors;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              setSubmitInProgress(true);
              _context.next = 3;
              return setShippingMethod(shippingMethodId);

            case 3:
              _yield$setShippingMet = _context.sent;
              dataError = _yield$setShippingMet.dataError;
              errors = dataError === null || dataError === void 0 ? void 0 : dataError.error;
              setSubmitInProgress(false);

              if (errors) {
                setGlobalErrors(errors);
              } else if ((globalErrors === null || globalErrors === void 0 ? void 0 : globalErrors.length) !== 0) {
                setGlobalErrors([]);
              }

            case 8:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function handleSetShippingMethod(_x) {
      return _ref6.apply(this, arguments);
    };
  }();

  var handleProcessPayment = /*#__PURE__*/function () {
    var _ref7 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(gateway, token, cardData, manualTransfer) {
      var _CHECKOUT_STEPS$find2;

      var _totalPrice$net, amount, result, createdPayment, manualTransferToken, paymentConfirmStepLink, _yield$createPayment, dataError, errors;

      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              setSubmitInProgress(true);

              if (!(gateway === GatewayTypesEnum.MANUAL_TRANSFER && manualTransfer)) {
                _context2.next = 36;
                break;
              }

              setSubmitInProgress(true);
              _context2.prev = 3;
              amount = totalPrice === null || totalPrice === void 0 ? void 0 : (_totalPrice$net = totalPrice.net) === null || _totalPrice$net === void 0 ? void 0 : _totalPrice$net.amount;

              if (!(!(checkout === null || checkout === void 0 ? void 0 : checkout.id) || amount == null)) {
                _context2.next = 8;
                break;
              }

              handlePaymentGatewayError([{
                message: "No se pudo procesar el pago."
              }]);
              return _context2.abrupt("return");

            case 8:
              _context2.next = 10;
              return createManualTransferCheckoutPayment({
                checkoutId: checkout.id,
                gateway: gateway,
                amount: amount,
                channelName: manualTransfer.channelName,
                receiptFile: manualTransfer.receiptFile
              });

            case 10:
              result = _context2.sent;

              if (!(result.errors && result.errors.length)) {
                _context2.next = 15;
                break;
              }

              handlePaymentGatewayError(result.errors.map(function (error) {
                return {
                  message: error.message || "Error al registrar el pago.",
                  field: error.field,
                  code: error.code
                };
              }));
              localStorage.removeItem("processPayment");
              return _context2.abrupt("return");

            case 15:
              setGlobalErrors([]);
              createdPayment = result.data && result.data.payment;
              manualTransferToken = createdPayment && createdPayment.token && createdPayment.token !== "manual_transfer" && createdPayment.token || manualTransfer.channelName;

              if (createdPayment && createdPayment.id && createdPayment.gateway) {
                syncSdkCheckoutPayment(saleor, {
                  id: createdPayment.id,
                  gateway: createdPayment.gateway,
                  token: manualTransferToken,
                  total: totalPrice && totalPrice.net ? {
                    amount: totalPrice.net.amount,
                    currency: totalPrice.net.currency
                  } : null,
                  clearCreditCard: true
                });
              }

              setSelectedPaymentGateway(gateway);
              setSelectedPaymentGatewayToken(manualTransferToken);

              if (!updateCheckoutFromBD) {
                _context2.next = 24;
                break;
              }

              _context2.next = 24;
              return updateCheckoutFromBD(!!user);

            case 24:
              if (!localStorage.getItem("processPayment")) {
                handleStepSubmitSuccess(CheckoutStep, CheckoutStep.Payment, getActiveStepIndex(), steps);
              }

              localStorage.removeItem("processPayment");
              _context2.next = 32;
              break;

            case 28:
              _context2.prev = 28;
              _context2.t0 = _context2["catch"](3);
              handlePaymentGatewayError([{
                message: "No se pudo enviar el comprobante. Verifica tu conexión e intenta de nuevo."
              }]);
              localStorage.removeItem("processPayment");

            case 32:
              _context2.prev = 32;
              setSubmitInProgress(false);
              return _context2.finish(32);

            case 35:
              return _context2.abrupt("return");

            case 36:
              paymentConfirmStepLink = (_CHECKOUT_STEPS$find2 = CHECKOUT_STEPS.find(function (step) {
                return step.step === CheckoutStep.PaymentConfirm;
              })) === null || _CHECKOUT_STEPS$find2 === void 0 ? void 0 : _CHECKOUT_STEPS$find2.link;
              _context2.next = 39;
              return createPayment({
                gateway: gateway,
                token: token,
                creditCard: cardData,
                returnUrl: "".concat(window.location.origin).concat(paymentConfirmStepLink)
              }, (user === null || user === void 0 ? void 0 : user.email) || "");

            case 39:
              _yield$createPayment = _context2.sent;
              dataError = _yield$createPayment.dataError;
              errors = dataError === null || dataError === void 0 ? void 0 : dataError.error;
              setSubmitInProgress(false);

              if (errors) {
                setGlobalErrors(errors);
                localStorage.removeItem("processPayment");
              } else {
                setGlobalErrors([]);

                if (!localStorage.getItem("processPayment")) {
                  handleStepSubmitSuccess(CheckoutStep, CheckoutStep.Payment, getActiveStepIndex(), steps);
                }

                localStorage.removeItem("processPayment");
              }

            case 44:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, null, [[3, 28, 32, 35]]);
    }));

    return function handleProcessPayment(_x2, _x3, _x4, _x5) {
      return _ref7.apply(this, arguments);
    };
  }();

  var handleSubmitPayment = /*#__PURE__*/function () {
    var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(paymentData) {
      var _response$data, _response$data2, _response$data3, _response$dataError;

      var response;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              _context3.next = 2;
              return completeCheckout({
                paymentData: paymentData
              }, (user === null || user === void 0 ? void 0 : user.email) || "");

            case 2:
              response = _context3.sent;
              return _context3.abrupt("return", {
                confirmationData: (_response$data = response.data) === null || _response$data === void 0 ? void 0 : _response$data.confirmationData,
                confirmationNeeded: (_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : _response$data2.confirmationNeeded,
                order: (_response$data3 = response.data) === null || _response$data3 === void 0 ? void 0 : _response$data3.order,
                errors: (_response$dataError = response.dataError) === null || _response$dataError === void 0 ? void 0 : _response$dataError.error
              });

            case 4:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));

    return function handleSubmitPayment(_x6) {
      return _ref8.apply(this, arguments);
    };
  }();

  var handleSubmitPaymentSuccess = React.useCallback(function (order) {
    setSubmitInProgress(false);
    setGlobalErrors([]);
    handleStepSubmitSuccess(CheckoutStep, CheckoutStep.Review, getActiveStepIndex(), steps, {
      id: order === null || order === void 0 ? void 0 : order.id,
      orderNumber: order === null || order === void 0 ? void 0 : order.number,
      token: order === null || order === void 0 ? void 0 : order.token
    });
  }, []);
  var handlePaymentGatewayError = React.useCallback(function (errors) {
    var _steps$find;

    setSubmitInProgress(false);
    setGlobalErrors(errors);
    var paymentStepLink = (_steps$find = steps.find(function (step) {
      return step.step === CheckoutStep.Payment;
    })) === null || _steps$find === void 0 ? void 0 : _steps$find.link;

    if (paymentStepLink) {
      navigate(paymentStepLink);
    }
  }, []);
  var paymentGatewaysForCheckout = checkoutPaymentGateways.length > 0 ? checkoutPaymentGateways : availablePaymentGateways || [];
  var paymentGatewaysView = checkoutLoaded && React.createElement(PaymentGatewaysList, {
    changeSubmitProgress: setSubmitInProgress,
    paymentGateways: paymentGatewaysForCheckout,
    processPayment: handleProcessPayment,
    submitPayment: handleSubmitPayment,
    submitPaymentSuccess: handleSubmitPaymentSuccess,
    formId: checkoutGatewayFormId,
    formRef: checkoutGatewayFormRef,
    selectedPaymentGateway: selectedPaymentGateway,
    selectedPaymentGatewayToken: selectedPaymentGatewayToken,
    selectPaymentGateway: setSelectedPaymentGateway,
    onError: handlePaymentGatewayError,
    totalPrice: totalPrice
  });
  var activeStep = getActiveStep();
  var buttonText = activeStep.name === CHECKOUT_STEPS[1].name && (checkout === null || checkout === void 0 ? void 0 : (_checkout$availableSh = checkout.availableShippingMethods) === null || _checkout$availableSh === void 0 ? void 0 : _checkout$availableSh.length) === 0 ? "Agregar dirección" : activeStep.nextActionName;
  /* eslint-disable default-case */

  switch (activeStep.nextActionName) {
    case "Continue to Shipping":
      buttonText = intl.formatMessage(checkoutMessages.addressNextActionName);
      break;

    case "Continue to Payment":
      buttonText = intl.formatMessage(checkoutMessages.shippingNextActionName);
      break;

    case "Continue to Review":
      buttonText = intl.formatMessage(checkoutMessages.paymentNextActionName);
      break;

    case "Realizar Pedido":
      if ((payment === null || payment === void 0 ? void 0 : payment.gateway) === "easypedido.payments.online_credibanco") {
        buttonText = intl.formatMessage(checkoutMessages.goToPay);
      } else {
        buttonText = intl.formatMessage(checkoutMessages.reviewNextActionName);
      }

      break;
  }

  var handlePaymentConfirm = /*#__PURE__*/function () {
    var _ref9 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
      var _steps$find2, paymentStepLink, _yield$completeChecko, data, dataError, errors, _steps$find3, _paymentStepLink, _data$order, _data$order2, _data$order3, _steps$find4, _paymentStepLink2;

      return _regeneratorRuntime.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              if (!controlPaymentConfirm) {
                _context4.next = 3;
                break;
              }

              setControlPaymentConfirm(false);
              return _context4.abrupt("return");

            case 3:
              /**
               * Prevent proceeding in confirmation flow in case of gateways that don't support it to prevent unknown bugs.
               */
              if ((payment === null || payment === void 0 ? void 0 : payment.gateway) !== "mirumee.payments.adyen") {
                paymentStepLink = (_steps$find2 = steps.find(function (step) {
                  return step.step === CheckoutStep.Payment;
                })) === null || _steps$find2 === void 0 ? void 0 : _steps$find2.link;

                if (paymentStepLink) {
                  navigate(paymentStepLink);
                }
              }

              setSubmitInProgress(true);
              setPaymentConfirmation(true);
              /**
               * Saleor API creates an order for not fully authorised payments, thus we accept all non negative payment result codes,
               * assuming the payment is completed, what means we can proceed further.
               * https://docs.adyen.com/checkout/drop-in-web?tab=http_get_1#step-6-present-payment-result
               */

              if (!adyenNotNegativeConfirmationStatusCodes.includes(querystring.resultCode)) {
                _context4.next = 17;
                break;
              }

              _context4.next = 9;
              return completeCheckout({}, (user === null || user === void 0 ? void 0 : user.email) || "");

            case 9:
              _yield$completeChecko = _context4.sent;
              data = _yield$completeChecko.data;
              dataError = _yield$completeChecko.dataError;
              errors = dataError === null || dataError === void 0 ? void 0 : dataError.error;
              setSubmitInProgress(false);

              if (errors) {
                setGlobalErrors(errors);
                _paymentStepLink = (_steps$find3 = steps.find(function (step) {
                  return step.step === CheckoutStep.Payment;
                })) === null || _steps$find3 === void 0 ? void 0 : _steps$find3.link;

                if (_paymentStepLink) {
                  navigate(_paymentStepLink);
                }
              } else {
                setGlobalErrors([]);
                handleStepSubmitSuccess(CheckoutStep, CheckoutStep.Review, getActiveStepIndex(), steps, {
                  id: data === null || data === void 0 ? void 0 : (_data$order = data.order) === null || _data$order === void 0 ? void 0 : _data$order.id,
                  orderNumber: data === null || data === void 0 ? void 0 : (_data$order2 = data.order) === null || _data$order2 === void 0 ? void 0 : _data$order2.number,
                  token: data === null || data === void 0 ? void 0 : (_data$order3 = data.order) === null || _data$order3 === void 0 ? void 0 : _data$order3.token
                });
              }

              _context4.next = 20;
              break;

            case 17:
              setGlobalErrors([{
                message: translateAdyenConfirmationError(querystring.resultCode, intl)
              }]);
              _paymentStepLink2 = (_steps$find4 = steps.find(function (step) {
                return step.step === CheckoutStep.Payment;
              })) === null || _steps$find4 === void 0 ? void 0 : _steps$find4.link;

              if (_paymentStepLink2) {
                setSubmitInProgress(false);
                setPaymentConfirmation(false);
                navigate(_paymentStepLink2);
              }

            case 20:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4);
    }));

    return function handlePaymentConfirm() {
      return _ref9.apply(this, arguments);
    };
  }();

  var activeStepIndex = getActiveStepIndex();

  var handleUpdateShippingMethodAndPrice = /*#__PURE__*/function () {
    var _ref10 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(id, gateway, token, isMounted) {
      return _regeneratorRuntime.wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              _context5.prev = 0;

              if (isMounted) {
                _context5.next = 3;
                break;
              }

              return _context5.abrupt("return");

            case 3:
              _context5.next = 5;
              return handleSetShippingMethod(id);

            case 5:
              _context5.next = 9;
              break;

            case 7:
              _context5.prev = 7;
              _context5.t0 = _context5["catch"](0);

            case 9:
              _context5.prev = 9;

              if (isMounted) {
                _context5.next = 12;
                break;
              }

              return _context5.abrupt("return");

            case 12:
              if (!(gateway && token)) {
                _context5.next = 16;
                break;
              }

              localStorage.setItem("processPayment", "true");
              _context5.next = 16;
              return handleProcessPayment(gateway, token);

            case 16:
              _context5.next = 20;
              break;

            case 18:
              _context5.prev = 18;
              _context5.t1 = _context5["catch"](9);

            case 20:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5, null, [[0, 7], [9, 18]]);
    }));

    return function handleUpdateShippingMethodAndPrice(_x7, _x8, _x9, _x10) {
      return _ref10.apply(this, arguments);
    };
  }();

  useEffect(function () {
    var isMounted = true;
    if (location.pathname !== "/checkout/review" && location.pathname !== "/checkout/payment") return;

    if (shippingPrice && dataCurrentTrade && (dataCurrentTrade === null || dataCurrentTrade === void 0 ? void 0 : dataCurrentTrade.currentTrade)) {
      var _newShippingMethod$, _newShippingMethod$$p;

      var newShippingMethod = dataCurrentTrade.currentShippingMethods.filter(function (shippingMethod) {
        var _shippingMethod$minim, _shippingMethod$maxim, _shippingMethod$minim2, _shippingMethod$maxim2, _shippingMethod$minim3, _shippingMethod$minim4;

        return (shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.maximumOrderPrice) !== undefined && (shippingMethod === null || shippingMethod === void 0 ? void 0 : (_shippingMethod$minim = shippingMethod.minimumOrderPrice) === null || _shippingMethod$minim === void 0 ? void 0 : _shippingMethod$minim.amount) !== undefined && (subtotalPrice === null || subtotalPrice === void 0 ? void 0 : subtotalPrice.net.amount) !== undefined && (shippingMethod === null || shippingMethod === void 0 ? void 0 : (_shippingMethod$maxim = shippingMethod.maximumOrderPrice) === null || _shippingMethod$maxim === void 0 ? void 0 : _shippingMethod$maxim.amount) !== undefined && (shippingMethod === null || shippingMethod === void 0 ? void 0 : (_shippingMethod$minim2 = shippingMethod.minimumOrderPrice) === null || _shippingMethod$minim2 === void 0 ? void 0 : _shippingMethod$minim2.amount) <= (subtotalPrice === null || subtotalPrice === void 0 ? void 0 : subtotalPrice.net.amount) && (shippingMethod === null || shippingMethod === void 0 ? void 0 : (_shippingMethod$maxim2 = shippingMethod.maximumOrderPrice) === null || _shippingMethod$maxim2 === void 0 ? void 0 : _shippingMethod$maxim2.amount) >= (subtotalPrice === null || subtotalPrice === void 0 ? void 0 : subtotalPrice.net.amount) || ((shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.maximumOrderPrice) === undefined || (shippingMethod === null || shippingMethod === void 0 ? void 0 : shippingMethod.maximumOrderPrice) === null) && (subtotalPrice === null || subtotalPrice === void 0 ? void 0 : subtotalPrice.net.amount) !== undefined && (shippingMethod === null || shippingMethod === void 0 ? void 0 : (_shippingMethod$minim3 = shippingMethod.minimumOrderPrice) === null || _shippingMethod$minim3 === void 0 ? void 0 : _shippingMethod$minim3.amount) !== undefined && (shippingMethod === null || shippingMethod === void 0 ? void 0 : (_shippingMethod$minim4 = shippingMethod.minimumOrderPrice) === null || _shippingMethod$minim4 === void 0 ? void 0 : _shippingMethod$minim4.amount) <= (subtotalPrice === null || subtotalPrice === void 0 ? void 0 : subtotalPrice.net.amount);
      });

      if ((newShippingMethod === null || newShippingMethod === void 0 ? void 0 : newShippingMethod.length) === 1 && ((_newShippingMethod$ = newShippingMethod[0]) === null || _newShippingMethod$ === void 0 ? void 0 : (_newShippingMethod$$p = _newShippingMethod$.price) === null || _newShippingMethod$$p === void 0 ? void 0 : _newShippingMethod$$p.amount) !== (shippingPrice === null || shippingPrice === void 0 ? void 0 : shippingPrice.amount)) {
        var _newShippingMethod$2;

        handleUpdateShippingMethodAndPrice((_newShippingMethod$2 = newShippingMethod[0]) === null || _newShippingMethod$2 === void 0 ? void 0 : _newShippingMethod$2.id, payment === null || payment === void 0 ? void 0 : payment.gateway, payment === null || payment === void 0 ? void 0 : payment.token, isMounted);
      }
    }

    return function () {
      isMounted = false;
    };
  }, [location.pathname, changeDataCurrentTrade]);
  useEffect(function () {
    if (updateCheckoutState) {
      updateCheckoutFromBD(true);
      setUpdateCheckoutState(false);
      localStorage.setItem("updateCheckoutPayment", "true");
    }
  }, [updateCheckoutState]);
  useEffect(function () {
    if ((!items || (items === null || items === void 0 ? void 0 : items.length) === 0) && !localStorage.getItem("order_token")) {
      navigate("/");
    }
  }, [items, items === null || items === void 0 ? void 0 : items.length]);
  return React.createElement(React.Fragment, null, React.createElement(MainMenu, {
    goback: true,
    search: false,
    cart: false,
    cartCheckout: true,
    mobileCartOpened: mobileCartOpened,
    setMobileCartOpened: setMobileCartOpened
  }), React.createElement(Checkout, {
    activeStep: activeStep.name,
    loading: submitInProgress || loadingPromoCode || !checkoutLoaded || !cartLoaded,
    navigation: getCheckoutProgress(!!(checkout === null || checkout === void 0 ? void 0 : checkout.shippingMethod), cartLoaded && checkoutLoaded, activeStepIndex, steps),
    cartSummary: prepareCartSummary(navigate, mobileCartOpened, setMobileCartOpened, totalPrice, subtotalPrice, shippingTaxedPrice, promoTaxedPrice, items, user === null || user === void 0 ? void 0 : (_user$currentTrade = user.currentTrade) === null || _user$currentTrade === void 0 ? void 0 : _user$currentTrade.defaultProductImageThumbnail),
    checkout: viewRouter,
    paymentGateways: paymentGatewaysView,
    paymentSelect: selectedPaymentGateway,
    hidePaymentGateways: steps[activeStepIndex].step !== CheckoutStep.Payment,
    button: getButton((_buttonText = buttonText) === null || _buttonText === void 0 ? void 0 : _buttonText.toUpperCase(), function () {
      var _checkout$availableSh2;

      handleNextStepClick(getActiveStep().index, (checkout === null || checkout === void 0 ? void 0 : (_checkout$availableSh2 = checkout.availableShippingMethods) === null || _checkout$availableSh2 === void 0 ? void 0 : _checkout$availableSh2.length) || 0);
    }, scaleButton)
  }), showVouchersList && React.createElement(AlertVouchersAvailableToUser, {
    hideModal: function hideModal() {
      return setShowVouchersList(false);
    },
    homeEntry: false,
    vouchersAvailableToUser: vouchersData === null || vouchersData === void 0 ? void 0 : vouchersData.vouchersAvailableToUser,
    setShowPromoCodeForm: setShowPromoCodeForm,
    setInputPromoValue: setInputPromoValue,
    checkoutPaymentSubpageRef: checkoutPaymentSubpageRef
  }), showErrors && React.createElement(AlertCheckoutErrors, {
    hideModal: function hideModal() {
      setShowErrors(false);
      setGlobalErrors([]);
    },
    completeCheckoutAgain: function completeCheckoutAgain() {
      var _checkoutReviewSubpag3;

      if ((_checkoutReviewSubpag3 = checkoutReviewSubpageRef.current) === null || _checkoutReviewSubpag3 === void 0 ? void 0 : _checkoutReviewSubpag3.complete) {
        var _checkoutReviewSubpag4;

        (_checkoutReviewSubpag4 = checkoutReviewSubpageRef.current) === null || _checkoutReviewSubpag4 === void 0 ? void 0 : _checkoutReviewSubpag4.complete();
      }
    },
    updatePaymentMethod: function updatePaymentMethod() {
      setUpdateCheckoutState(true);
    },
    errors: globalErrors
  }), !checkoutLoaded && React.createElement(UpdateCheckout, null));
};

export { CheckoutPage };